import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import type { GenericField, OId } from '@Types';
import type { FieldInitialSettings, FieldSchema, RawField } from '@FormModule';
import { useForm } from '@FormModule';
import { useAnimalProfileStore } from '@Stores';

interface MedicalStaff {
  [key: string]: any;
  first_name: string;
  last_name: string;
  _id: OId;
}

const currentSchemaModels = ref<Set<GenericField>>(new Set());
const fieldsSchema = ref<FieldSchema[]>([]);
const formError = ref();

export default function () {
  const { t } = useI18n();
  const { getDetailsSchema, wasFormSubmitted } = useForm();
  const { medicalStaff, surgeryTemplates } = storeToRefs(useAnimalProfileStore());

  const rawFields: RawField[] = [
    {
      label: 'Surgery',
      name: 'template_name',
      required: true,
      type: 'select',
      value: '',
    },
    {
      label: 'Status',
      name: 'action',
      required: true,
      type: 'radio',
      value: '',
      values: [t('common.schedule'), t('common.complete')],
    },
    {
      label: 'Surgery Date',
      name: 'date',
      required: true,
      type: 'date',
      value: '',
    },
    {
      label: 'Attending Vet',
      name: 'attending_vet',
      required: true,
      type: 'select',
      value: '',
    },
    {
      label: 'Other Veterinarian',
      name: 'other_by',
      required: false,
      type: 'text',
      value: '',
    },
    {
      label: 'Notes',
      name: 'notes',
      required: false,
      type: 'textarea',
      value: '',
    },
    {
      label: 'Schedule a Follow-Up Check',
      name: 'schedule_followup',
      required: false,
      type: 'toggle',
      value: '',
    },
    {
      label: 'Time Until Follow-Up',
      name: 'time_until_followup',
      required: false,
      type: 'timeframe',
      value: '',
    },
    {
      label: 'Follow-Up Reason',
      name: 'followup_reason',
      required: false,
      type: 'textarea',
      value: '',
    },
    {
      label: 'Follow-Up Notes',
      name: 'followup_notes',
      required: false,
      type: 'textarea',
      value: '',
    },
  ];

  const attendingVetOptions = computed(() =>
    medicalStaff.value.map((staff: MedicalStaff) => {
      const { first_name, last_name } = staff;
      return {
        ...staff,
        by: `${first_name} ${last_name}`,
      };
    })
  );

  const fieldsSettings = computed(
    (): FieldInitialSettings => ({
      action: {
        settings: {
          hint: 'animals.individualPage.medical.medications.completeMedicationHint',
        },
      },
      template_name: {
        edit: {
          label: 'template_name',
          options: surgeryTemplates.value.sort((a, b) =>
            a.template_name.localeCompare(b.template_name)
          ),
        },
      },
      attending_vet: {
        edit: {
          label: 'by',
          options: [...attendingVetOptions.value, { by: t('common.other') }],
        },
        settings: {
          required: false,
          show: false,
        },
      },
      other_by: {
        settings: {
          show: false,
        },
      },
    })
  );

  const surgeryTemplateField = computed(() =>
    fieldsSchema.value.find(({ name }) => name === 'template_name')
  );
  const followUpFields = computed(() =>
    fieldsSchema.value.find(({ name }) => name === 'schedule_followup')
  );
  const byField = computed(() => fieldsSchema.value.find(({ name }) => name === 'attending_vet'));
  const byModel = computed(() => byField.value?.model);
  const actionField = computed(() => fieldsSchema.value.find(({ name }) => name === 'action'));
  const isAction = computed(() => actionField.value?.model);
  const isFollowUp = computed(() => followUpFields.value?.model);
  const surgeryTemplateModel = computed(() => surgeryTemplateField.value?.model);

  watch(isAction, () => showActionFields());
  watch(isFollowUp, () => showFollowUpFields());
  watch(surgeryTemplateModel, (newValue, oldValue) => fillFormWithTemplate(newValue, oldValue));
  watch(byModel, () => showOtherVetField());

  function showOtherVetField() {
    fieldsSchema.value.forEach((field) => {
      if (field.name === 'other_by') {
        const showField = byModel?.value?.by === t('common.other');

        field.settings = {
          ...field.settings,
          show: showField,
          required: showField,
        };

        if (byModel?.value?.by !== t('common.other')) {
          field.model = '';
          field.value = '';
          field.state.errors.clear();
        }
      }
    });

    return true;
  }

  function fillFormWithTemplate(newValue: any, oldValue: any) {
    if (!newValue || oldValue === newValue) return;

    updateSchemaModels(surgeryTemplateModel.value);
  }

  function showActionFields() {
    fieldsSchema.value.forEach((field) => {
      if (field.name === 'attending_vet') {
        const showField = isAction.value === 'Complete';

        field.settings = {
          ...field.settings,
          required: showField,
          show: showField,
        };
        field.model = '';
        field.value = '';
        field.state.errors.clear();
      }
    });

    return true;
  }

  function showFollowUpFields() {
    fieldsSchema.value.forEach((field) => {
      const followUpFields = ['time_until_followup', 'followup_reason', 'followup_notes'];

      if (followUpFields.includes(field.name)) {
        const showField = isFollowUp.value;

        field.settings = {
          ...field.settings,
          required: field.name === 'time_until_followup' ? showField : false,
          show: showField,
        };
        field.state.errors.clear();
      }
    });

    return true;
  }

  function setForm() {
    fieldsSchema.value = getDetailsSchema(rawFields, fieldsSettings.value);

    if (!surgeryTemplateModel.value) {
      fieldsSchema.value.forEach((field: any) => {
        if (field.name === 'template_name') return;

        field.state.disabled = true;
        field.settings.disabled = true;
      });
    }
  }

  function updateSchemaModels(sourceData?: any) {
    const toggleableFields = [
      'time_until_followup',
      'followup_reason',
      'followup_notes',
      'template_name',
    ];

    formError.value = null;
    wasFormSubmitted.value = false;

    fieldsSchema.value.forEach((field) => {
      field.state.disabled = false;
      field.settings.disabled = false;

      const match = sourceData[field.name];
      if (toggleableFields.includes(field.name)) return;

      switch (field.name) {
        case 'action': {
          setMatchStatusField(field, sourceData);
          break;
        }
        case 'attending_vet': {
          setByField(field, sourceData);
          break;
        }
        case 'other_by': {
          setOtherByField(field, sourceData);
          break;
        }
        case 'schedule_followup': {
          setMatchField(field, match);
          showFollowUpFields();
          nextTick(() => {
            setMatchFollowUpField(sourceData);
          });
          break;
        }
        case 'date': {
          field.model = new Date();
          field.value = new Date();
          break;
        }
        default: {
          setMatchField(field, match);
        }
      }
    });
  }

  function setMatchField(field: any, match: any) {
    field.model = match;
    field.value = match;
  }

  function setMatchStatusField(field: any, sourceData: any) {
    field.model = sourceData.status === 'active' ? 'Schedule' : 'Complete';
    field.value = sourceData.status === 'active' ? 'Schedule' : 'Complete';
    field.state.errors.clear();
  }

  function setByField(field: any, sourceData: any) {
    if (!sourceData.by) return;

    if (sourceData.by_id === 'other') {
      field.model = { by: 'Other' };
      field.value = { by: 'Other' };

      return;
    }

    const [first_name, last_name] = sourceData.by.split(' ');
    const by = { _id: sourceData.by_id, first_name, last_name, by: sourceData.by };

    field.model = by;
    field.value = by;
  }

  function setOtherByField(field: any, sourceData: any) {
    if (!sourceData.by) return;

    field.model = sourceData.by;
    field.value = sourceData.by;
  }

  function setMatchFollowUpField(sourceData: any) {
    const followUpFields = ['time_until_followup', 'followup_notes'];

    fieldsSchema.value.forEach((field) => {
      if (!followUpFields.includes(field.name)) return;

      field.state.errors.clear();

      const timeFrame = {
        duration: sourceData.time_until_followup,
        duration_unit: sourceData.followup_unit,
      };

      switch (field.name) {
        case 'time_until_followup': {
          field.model = timeFrame;
          field.value = timeFrame;
          break;
        }
        case 'followup_notes': {
          field.model = sourceData.followup_notes;
          field.value = sourceData.followup_notes;
          break;
        }
        case 'followup_reason': {
          field.model = sourceData.followup_reason;
          field.value = sourceData.followup_reason;
          break;
        }
      }
    });
  }

  function resetForm() {
    currentSchemaModels.value = new Set();
    fieldsSchema.value = [];
  }

  return {
    currentSchemaModels,
    fieldsSchema,
    formError,
    rawFields,

    resetForm,
    setForm,
  };
}
